import delayUtils from '@/utils/delay'
import useRouteLoading from '@/hooks/useRouteLoading'

type LazyLoader = () => Promise<{
  default?: Any
  [key: string]: Any
}>

interface LazyLoadOptions<M> {
  delay: number
  loader: M
}

const loadContainer = async <T extends LazyLoader>(loader: T, delay: number = 100) => {
  const { stop, start } = useRouteLoading()

  const { done } = delayUtils.cancel(delay, { task: start })

  try {
    return await Promise.resolve(loader())
  } catch (error) {
    // Vite preload (especially CSS) can fail under poor international networks.
    // Retry once before surfacing the error.
    await new Promise((r) => setTimeout(r, 800))
    return await Promise.resolve(loader())
  } finally {
    done(stop)
  }
}

const lazyLoad = <T extends LazyLoader>(source: T | LazyLoadOptions<T>) => {
  if (typeof source === 'object') {
    const { loader, delay } = source

    return loadContainer(loader, delay)
  }

  return loadContainer(source)
}

export default lazyLoad
